/**
 * Device Type Detection Utilities
 * Ported from React Native app for consistent device type detection
 */

// Device type constants
export const DeviceType = {
  KBEACON: 'kbeacon',
  ELA: 'ela',
  SIGMAWIT: 'sigmawit',
  T201: 't201',
  BLOGM2: 'blogm2',
  UNKNOWN: 'unknown'
};

/**
 * Detect device type based on sensor name and properties
 * @param {Object} sensor - Sensor object with name, type, deviceType properties
 * @returns {string} Detected device type
 */
export function detectDeviceType(sensor) {
  if (!sensor) return DeviceType.UNKNOWN;
  
  const { name, type, deviceType } = sensor;
  
  // Check explicit device type first
  if (deviceType) {
    const normalizedType = deviceType.toLowerCase();
    if (Object.values(DeviceType).includes(normalizedType)) {
      return normalizedType;
    }
  }
  
  // Check type property
  if (type) {
    const normalizedType = type.toLowerCase();
    if (Object.values(DeviceType).includes(normalizedType)) {
      return normalizedType;
    }
  }
  
  // Detect based on name patterns
  const sensorName = (name || '').toLowerCase();
  
  // SIGMAWIT devices
  if (sensorName.includes('sigmawit') || 
      sensorName.includes('t201') || 
      sensorName.includes('blogm2') ||
      sensorName.includes('s6')) {
    // More specific detection for T201 and BLogm2
    if (sensorName.includes('t201')) return DeviceType.T201;
    if (sensorName.includes('blogm2')) return DeviceType.BLOGM2;
    return DeviceType.SIGMAWIT;
  }
  
  // ELA devices
  if (sensorName.includes('ela') || 
      sensorName.includes('p t en') ||
      /^[A-F0-9]{6,8}$/i.test(sensorName)) { // Hex pattern for ELA tags
    return DeviceType.ELA;
  }
  
  // KBeacon devices
  if (sensorName.includes('kbeacon') ||
      sensorName.includes('kb_') ||
      sensorName.includes('kbpro_') ||
      sensorName.startsWith('kb')) {
    return DeviceType.KBEACON;
  }
  
  return DeviceType.UNKNOWN;
}

/**
 * Check if sensor is a SIGMAWIT device
 * @param {Object} sensor - Sensor object
 * @returns {boolean} True if SIGMAWIT device
 */
export function isSIGMAWITDevice(sensor) {
  const deviceType = detectDeviceType(sensor);
  return deviceType === DeviceType.SIGMAWIT || 
         deviceType === DeviceType.T201 || 
         deviceType === DeviceType.BLOGM2;
}

/**
 * Check if sensor is an ELA device
 * @param {Object} sensor - Sensor object
 * @returns {boolean} True if ELA device
 */
export function isELADevice(sensor) {
  return detectDeviceType(sensor) === DeviceType.ELA;
}

/**
 * Check if sensor is a KBeacon device
 * @param {Object} sensor - Sensor object
 * @returns {boolean} True if KBeacon device
 */
export function isKBeaconDevice(sensor) {
  return detectDeviceType(sensor) === DeviceType.KBEACON;
}

/**
 * Check if sensor is a T201 device (specific SIGMAWIT model)
 * @param {Object} sensor - Sensor object
 * @returns {boolean} True if T201 device
 */
export function isT201Device(sensor) {
  return detectDeviceType(sensor) === DeviceType.T201;
}

/**
 * Check if sensor is a BLogm2 device (specific SIGMAWIT model)
 * @param {Object} sensor - Sensor object
 * @returns {boolean} True if BLogm2 device
 */
export function isBLogm2Device(sensor) {
  return detectDeviceType(sensor) === DeviceType.BLOGM2;
}

/**
 * Get device-specific battery percentage conversion
 * @param {number} batteryLevel - Raw battery level
 * @param {Object} sensor - Sensor object
 * @returns {number} Battery percentage (0-100)
 */
export function getBatteryPercentage(batteryLevel, sensor) {
  if (!batteryLevel || batteryLevel <= 0 || isNaN(batteryLevel)) {
    return 0;
  }

  const deviceType = detectDeviceType(sensor);

  // SIGMAWIT devices: Battery is stored in mV (1500-4000mV range)
  if (isSIGMAWITDevice(sensor)) {
    // Check if value is already in mV range (1500-4000)
    if (batteryLevel >= 1500 && batteryLevel <= 4000) {
      // Convert mV to percentage: (mV - 1500) / 25
      const percentage = (batteryLevel - 1500) / 25;
      return Math.min(100, Math.max(0, Math.round(percentage)));
    }
    // If value is already in percentage range (0-100), return as-is
    if (batteryLevel >= 0 && batteryLevel <= 100) {
      return Math.min(100, Math.max(0, Math.round(batteryLevel)));
    }
    // Fallback: assume it's mV and convert
    const percentage = (batteryLevel - 1500) / 25;
    return Math.min(100, Math.max(0, Math.round(percentage)));
  }

  // ELA devices: Battery can be sent as percentage (0-100) or voltage in mV
  if (isELADevice(sensor)) {
    // Check if value is already in percentage range (0-100)
    if (batteryLevel >= 0 && batteryLevel <= 100) {
      const percentage = Math.min(100, Math.max(0, Math.round(batteryLevel)));
      return percentage;
    }
    
    // Check if value is in ELA voltage range (1500-4000mV, typical 2800-3200mV)
    if (batteryLevel >= 1500 && batteryLevel <= 4000) {
      // Convert voltage to percentage: (voltage - 2800) / 400 * 100
      const percentage = ((batteryLevel - 2800) / 400) * 100;
      const rounded = Math.min(100, Math.max(0, Math.round(percentage)));
      return rounded;
    }
    
    // Fallback: try standard ELA conversion
    const percentage = ((batteryLevel - 2800) / 400) * 100;
    const rounded = Math.min(100, Math.max(0, Math.round(percentage)));
    return rounded;
  }

  // KBeacon devices: Convert mV to approximate percentage
  // 3300mV = 100%, 2000mV = 0%
  const percentage = Math.min(100, Math.max(0, ((batteryLevel - 2000) / (3300 - 2000)) * 100));
  return Math.round(percentage);
}

/**
 * Get device-specific battery color
 * @param {number} batteryLevel - Raw battery level
 * @param {Object} sensor - Sensor object
 * @returns {string} Color code for battery level
 */
export function getBatteryColor(batteryLevel, sensor) {
  if (!batteryLevel || batteryLevel <= 0) {
    return '#FF453A'; // Red - No battery data
  }

  const percentage = getBatteryPercentage(batteryLevel, sensor);
  
  if (percentage >= 70) return '#34C759'; // Green - Good
  if (percentage >= 30) return '#FF9500'; // Orange - Medium
  return '#FF453A'; // Red - Low
}

/**
 * Get device display name (cleaned up for UI)
 * @param {Object} sensor - Sensor object
 * @returns {string} Clean display name
 */
export function getDeviceDisplayName(sensor) {
  if (!sensor || !sensor.name) return 'Unknown Sensor';
  
  const deviceType = detectDeviceType(sensor);
  const name = sensor.name;
  
  // For ELA devices with hex names, return a more readable name
  if (deviceType === DeviceType.ELA && /^[A-F0-9]{6,8}$/i.test(name)) {
    return `ELA Tag (${name})`;
  }
  
  return name;
}
